// ============================================================================
// Create-invoice-draft modal (Phase 1.5). The user picks a facility + period,
// maps each dept's unbilled hours to one of the facility's APPROVED
// (Active-contract) Hourly_Position rate lines, optionally adds approved
// Recurring_Monthly lines, and submits. Rates are never typed here — they can
// only be selected from approved contract lines (governance: BDD/AM author,
// finance/exec approve). create_invoice_draft_v2 enforces the same rule.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, X } from 'lucide-react';
import { Button, ErrorState, useToast } from '../../shared/components';
import { fmtHours, fmtMoney } from './types';
import type { DeptRateMapEntry, QueueFacility } from './types';
import {
  createInvoiceDraftV2,
  useFacilityContractRates,
} from './hooks/use-invoicing-data';
import { errorMessage } from '../../shared/errors';

interface CreateDraftModalProps {
  facilities: QueueFacility[];
  initialFacilityId?: string | null;
  onClose: () => void;
  onCreated?: (invoiceId: string) => void;
}

export function CreateDraftModal({
  facilities,
  initialFacilityId,
  onClose,
  onCreated,
}: CreateDraftModalProps) {
  const navigate = useNavigate();
  const toast = useToast();

  const [facilityId, setFacilityId] = useState<string>(
    initialFacilityId ?? facilities[0]?.facility_id ?? '',
  );
  const facility = useMemo(
    () => facilities.find((f) => f.facility_id === facilityId) ?? null,
    [facilities, facilityId],
  );

  const { data: rates, isLoading: ratesLoading, error: ratesError } =
    useFacilityContractRates(facilityId || null);

  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  // dept -> selected contract_line_id ('' = skip this dept)
  const [deptMap, setDeptMap] = useState<Record<string, string>>({});
  const [monthlySelected, setMonthlySelected] = useState<Record<string, boolean>>({});
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Default the period to the facility's unbilled week span when it changes.
  useEffect(() => {
    if (facility) {
      setPeriodStart(facility.earliest_week ?? '');
      setPeriodEnd(facility.latest_week ?? '');
    }
  }, [facility]);

  // Auto-map each dept to a same-named approved hourly line when rates arrive.
  useEffect(() => {
    if (!facility || !rates) return;
    const next: Record<string, string> = {};
    for (const d of facility.depts) {
      const match = rates.hourlyLines.find(
        (l) => l.description.trim().toLowerCase() === d.dept.trim().toLowerCase(),
      );
      next[d.dept] = match?.line_id ?? '';
    }
    setDeptMap(next);
  }, [facility, rates]);

  const hourlyLines = rates?.hourlyLines ?? [];
  const monthlyLines = rates?.monthlyLines ?? [];
  const noContract = !!rates && rates.contractId === null;

  const mappedDeptCount = facility
    ? facility.depts.filter((d) => deptMap[d.dept]).length
    : 0;
  const monthlyCount = Object.values(monthlySelected).filter(Boolean).length;
  const canSubmit =
    !!facility &&
    !!periodStart &&
    !!periodEnd &&
    periodEnd >= periodStart &&
    (mappedDeptCount > 0 || monthlyCount > 0) &&
    !submitting;

  // Estimated total from the chosen rates × dept hours (display only; the RPC
  // is the source of truth and itemizes per weekly billing line).
  const estTotal = useMemo(() => {
    if (!facility) return 0;
    let total = 0;
    for (const d of facility.depts) {
      const lineId = deptMap[d.dept];
      if (!lineId) continue;
      const rate = hourlyLines.find((l) => l.line_id === lineId)?.unit_rate ?? 0;
      total += d.hours * Number(rate);
    }
    for (const m of monthlyLines) {
      if (monthlySelected[m.line_id]) total += Number(m.unit_rate ?? 0);
    }
    return total;
  }, [facility, deptMap, monthlySelected, hourlyLines, monthlyLines]);

  async function handleSubmit() {
    if (!facility) return;
    const deptRateMap: DeptRateMapEntry[] = facility.depts
      .filter((d) => deptMap[d.dept])
      .map((d) => ({ dept: d.dept, contract_line_id: deptMap[d.dept] }));
    const monthlyLineIds = monthlyLines
      .filter((m) => monthlySelected[m.line_id])
      .map((m) => m.line_id);

    setSubmitting(true);
    setSubmitError(null);
    try {
      const invoiceId = await createInvoiceDraftV2({
        facilityId: facility.facility_id,
        periodStart,
        periodEnd,
        deptRateMap,
        monthlyLineIds,
        notes: notes.trim() || null,
      });
      toast.success(`Draft ${invoiceId} created`, { description: facility.facility_name });
      onCreated?.(invoiceId);
      navigate(`/invoicing/${invoiceId}`);
    } catch (err) {
      setSubmitError(errorMessage(err, 'Failed to create the draft.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-tcs-scrim/60 p-0 sm:items-center sm:p-4">
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-xl bg-tcs-bg shadow-xl sm:rounded-xl">
        <header className="flex items-center justify-between border-b border-tcs-border px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-tcs-text">New invoice draft</h2>
            <p className="text-xs text-tcs-text-muted">
              Rates come from the facility's approved contract — select, don't type.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-tcs-text-muted hover:text-tcs-text"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="min-h-0 flex-1 space-y-4 overflow-auto px-5 py-4">
          {/* Facility */}
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
              Facility
            </span>
            <select
              value={facilityId}
              onChange={(e) => setFacilityId(e.target.value)}
              className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
            >
              {facilities.map((f) => (
                <option key={f.facility_id} value={f.facility_id}>
                  {f.facility_name} · {fmtHours(f.total_hours)} hrs · {f.total_lines} lines
                </option>
              ))}
            </select>
          </label>

          {/* Period */}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
                Period start
              </span>
              <input
                type="date"
                value={periodStart}
                onChange={(e) => setPeriodStart(e.target.value)}
                className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
                Period end
              </span>
              <input
                type="date"
                value={periodEnd}
                onChange={(e) => setPeriodEnd(e.target.value)}
                className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
              />
            </label>
          </div>

          {ratesLoading && (
            <div className="flex items-center gap-2 text-xs text-tcs-text-muted">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading contract rates…
            </div>
          )}
          {ratesError && <ErrorState title="Couldn't load contract rates" body={ratesError} />}

          {noContract && (
            <div className="rounded-md border border-st-warn-border-strong bg-st-warn-bg px-3 py-3 text-sm text-st-warn-fg-strong">
              <p className="font-medium">No Active contract for this facility.</p>
              <p className="mt-1 text-xs">
                Rates must come from an approved contract. Author one (with a rate line per
                dept), take it through finance/executive approval to <strong>Active</strong>,
                then return here.
              </p>
              <Button
                as="a"
                href="/contracts/new"
                variant="secondary"
                size="sm"
                className="mt-2"
              >
                Go to Contracts
              </Button>
            </div>
          )}

          {/* Dept -> rate mapping */}
          {facility && !noContract && (
            <div>
              <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
                Hours by dept → rate
              </h3>
              <div className="space-y-2">
                {facility.depts.map((d) => {
                  const selected = deptMap[d.dept] ?? '';
                  const rate = hourlyLines.find((l) => l.line_id === selected)?.unit_rate ?? null;
                  return (
                    <div
                      key={d.dept}
                      className="grid grid-cols-12 items-center gap-2 rounded-md border border-tcs-border bg-tcs-raised px-3 py-2"
                    >
                      <div className="col-span-4 min-w-0">
                        <div className="truncate text-sm font-medium text-tcs-text">{d.dept}</div>
                        <div className="text-[11px] text-tcs-text-muted">
                          {fmtHours(d.hours)} hrs · {d.lines} lines
                        </div>
                      </div>
                      <div className="col-span-5">
                        <select
                          value={selected}
                          onChange={(e) =>
                            setDeptMap((m) => ({ ...m, [d.dept]: e.target.value }))
                          }
                          className="block w-full rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5 text-xs text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
                        >
                          <option value="">— skip (leave unbilled) —</option>
                          {hourlyLines.map((l) => (
                            <option key={l.line_id} value={l.line_id}>
                              {l.description} ({fmtMoney(l.unit_rate)}/{l.unit_label || 'hr'})
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-span-3 text-right font-mono text-xs text-tcs-text">
                        {rate != null ? fmtMoney(d.hours * Number(rate)) : '—'}
                      </div>
                    </div>
                  );
                })}
                {hourlyLines.length === 0 && (
                  <p className="rounded-md border border-dashed border-tcs-border bg-tcs-bg-muted px-3 py-2 text-xs text-tcs-text-muted">
                    The Active contract has no Hourly_Position rate lines. Add them in Contracts
                    before billing hours.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Optional monthly lines */}
          {facility && !noContract && monthlyLines.length > 0 && (
            <div>
              <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
                Recurring monthly lines (optional)
              </h3>
              <div className="space-y-1.5">
                {monthlyLines.map((m) => (
                  <label
                    key={m.line_id}
                    className="flex items-center gap-2 rounded-md border border-tcs-border bg-tcs-raised px-3 py-2 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={!!monthlySelected[m.line_id]}
                      onChange={(e) =>
                        setMonthlySelected((s) => ({ ...s, [m.line_id]: e.target.checked }))
                      }
                    />
                    <span className="flex-1 truncate text-tcs-text">{m.description}</span>
                    <span className="font-mono text-xs text-tcs-text">{fmtMoney(m.unit_rate)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-tcs-text-muted">
              Notes (optional)
            </span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="block w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
            />
          </label>

          {submitError && <ErrorState title="Couldn't create the draft" body={submitError} />}
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-tcs-border px-5 py-3">
          <div className="text-sm text-tcs-text-secondary">
            Est. total <span className="font-mono font-semibold text-tcs-text">{fmtMoney(estTotal)}</span>
            <span className="ml-2 text-xs text-tcs-text-muted">
              {mappedDeptCount} dept{mappedDeptCount === 1 ? '' : 's'}
              {monthlyCount > 0 ? ` · ${monthlyCount} monthly` : ''}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSubmit} disabled={!canSubmit} loading={submitting}>
              Create draft
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}
